// @givmo/board — every written consent this board has opened, and where each one stands.
//
// Drafting is Member+Owner work (`board_record:write`) — a draft has no roster and cannot be signed, so
// it is recording work like any other. Everything that asks the board to ACT is Owner-only
// (`board_consent:manage`): circulating fixes who must sign, revising resets a count, withdrawing ends
// the instrument, and recording an assent is the officer writing down somebody else's decision. This
// page shows the Owner-only controls to a Member with a plain line about who can use them, per the
// module's disclosure-not-concealment posture — the backend refuses regardless.
//
// The list is filtered CLIENT-side even though the route accepts a `consent_status` query. Boards open
// a handful of consents a year, the whole set arrives in one page, and a server round trip per chip
// would make the filter feel slower than it is. If a board ever outgrows one page the filter moves to
// the query — the decode already reads the paginated envelope.

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '../../board-routing';
import { type OrgBoardWrittenConsentOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FilterBar,
  FilterChip,
  Icon,
  Skeleton,
} from '@givmo/design-system';
import { FileSignature } from 'lucide-react';
import { ConnectedAiDraftBadge } from '../../components/connected-ai-draft-badge';
import { PageShell } from '../../components/page-shell';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { ConsentDraftDialog } from './consent-draft-dialog';
import {
  consentCommitteeName,
  consentStatusLabel,
  consentStatusTone,
  displayDate,
  extractConsents,
  sortConsents,
} from './consents-decode';
import { useBoardOperations } from '../../board-runtime';

/** The filter chips, in lifecycle order. `null` is "everything". */
const STATUS_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'pending', label: 'Waiting on signatures' },
  { value: 'executed', label: 'Signed by everyone' },
  { value: 'withdrawn', label: 'Stopped' },
];

export function consentsQueryKey(orgRef: string | null): readonly unknown[] {
  return ['board-written-consents', orgRef];
}

export function ConsentsPage() {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  // Starting a consent creates its draft — the backend's `board_record:write`, not the manage string.
  const canDraft = hasBoardPermission(permissions, 'board_record:write');
  const [drafting, setDrafting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const consentsQuery = useQuery({
    queryKey: consentsQueryKey(orgRef),
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardWrittenConsentOut[]> => {
      const { data } = await operations.officerListBoardWrittenConsents({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return sortConsents(extractConsents(data));
    },
  });

  const consents = consentsQuery.data ?? [];
  const shown = useMemo(
    () =>
      statusFilter === null
        ? consents
        : consents.filter((consent) => consent.status?.toLowerCase() === statusFilter),
    [consents, statusFilter],
  );
  const countFor = (value: string | null): number =>
    value === null
      ? consents.length
      : consents.filter((consent) => consent.status?.toLowerCase() === value).length;

  return (
    <PageShell
      title="Written consents"
      description="How your board, or one of its committees, acts without holding a meeting. Every seat it goes out to has to sign before it becomes an act of the board, or of the committee whose consent it is."
      actions={
        // Starting a consent is the draft door, `board_record:write`: without it, no button.
        canDraft ? (
          <Button
            appearance="saas"
            tone="primary"
            disabled={!isReady || orgRef === null}
            onClick={() => setDrafting(true)}
            testId="consents-start"
          >
            Start one
          </Button>
        ) : undefined
      }
      testId="page-consents"
    >
      {consents.length > 0 ? (
        <FilterBar label="Show" ariaLabel="Filter consents by state" testId="consents-filter">
          {STATUS_FILTERS.map((filter) => (
            <FilterChip
              key={filter.label}
              label={filter.label}
              count={countFor(filter.value)}
              selected={statusFilter === filter.value}
              onToggle={() => setStatusFilter(filter.value)}
              testId={`consents-filter-${filter.value ?? 'all'}`}
            />
          ))}
        </FilterBar>
      ) : null}

      {consentsQuery.isPending && isReady ? (
        <div className="flex flex-col gap-12" data-testid="consents-loading">
          <Skeleton className="h-84 w-full rounded-lg" />
          <Skeleton className="h-84 w-full rounded-lg" />
        </div>
      ) : null}

      {consentsQuery.isError ? (
        <Card testId="consents-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this board’s written consents.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void consentsQuery.refetch()}
              testId="consents-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {consentsQuery.isSuccess && consents.length === 0 ? (
        <EmptyState
          testId="consents-empty"
          icon={<Icon icon={FileSignature} size={20} aria-hidden />}
          title="No written consents yet"
          description={
            canDraft
              ? 'When your board needs to act between meetings, this is where that happens — draft it, put it to your board, and it becomes a record once everyone has signed.'
              : 'When your board needs to act between meetings, this is where that happens.'
          }
          action={
            canDraft ? (
              <Button
                appearance="saas"
                tone="primary"
                onClick={() => setDrafting(true)}
                testId="consents-empty-start"
              >
                Start one
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {consentsQuery.isSuccess && consents.length > 0 && shown.length === 0 ? (
        <p
          className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          data-testid="consents-none-in-filter"
        >
          Nothing here in that state.
        </p>
      ) : null}

      {shown.map((consent) => (
        <ConsentCard key={consent.id} consent={consent} />
      ))}

      {drafting && orgRef !== null ? (
        <ConsentDraftDialog
          orgRef={orgRef}
          onClose={() => setDrafting(false)}
          onCreated={(consent) => {
            setDrafting(false);
            void queryClient.invalidateQueries({ queryKey: consentsQueryKey(orgRef) });
            void navigate({ to: '/consents/$consentId', params: { consentId: consent.id } });
          }}
        />
      ) : null}
    </PageShell>
  );
}

function ConsentCard({ consent }: { consent: OrgBoardWrittenConsentOut }) {
  const version = consent.version ?? null;
  // CBG-YR6-CONSENT: a committee's consent leads its line with the committee, as its meetings do.
  const committee = consentCommitteeName(consent);
  const versionCount = consent.version_count ?? 0;
  const required = consent.required_count ?? 0;

  return (
    <Card testId={`consents-card-${consent.id}`}>
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-4">
            <Link
              to="/consents/$consentId"
              params={{ consentId: consent.id }}
              className="font-sans text-s16 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid={`consents-link-${consent.id}`}
            >
              {version?.title ?? 'Untitled consent'}
            </Link>
            <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
              {committee !== null ? (
                <span
                  className="font-w600 text-text-secondary"
                  data-testid={`consents-committee-${consent.id}`}
                >
                  {committee} ·{' '}
                </span>
              ) : null}
              Dated {displayDate(consent.consent_date)}
              {versionCount > 1 ? ` · version ${version?.version_number ?? versionCount}` : ''}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-8">
            <ConnectedAiDraftBadge
              source={version}
              testId={`consents-connected-ai-draft-${consent.id}`}
            />
            {required > 0 ? (
              <Badge tone="neutral">
                {consent.signed_count ?? 0} of {required} signed
              </Badge>
            ) : null}
            <Badge tone={consentStatusTone(consent.status)}>
              {consentStatusLabel(consent.status)}
            </Badge>
          </div>
        </div>

        {versionCount > 1 ? (
          <p
            className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            data-testid={`consents-versions-${consent.id}`}
          >
            The wording has changed {versionCount - 1} {versionCount - 1 === 1 ? 'time' : 'times'}.
            Earlier wordings, and the signatures given on them, are kept with this consent.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
